/**
 * Pagination utility functions to ensure valid pagination parameters
 */

export interface PaginationParams {
  page: number;
  pageSize: number;
}

export interface PaginationResult {
  page: number;
  pageSize: number;
  skip: number;
  limit: number;
}

/**
 * Validates and normalizes pagination parameters
 * Ensures page is at least 1 and pageSize is between 1 and 1000
 */
export function validatePaginationParams(
  page: number | string | null | undefined,
  pageSize: number | string | null | undefined = 10
): PaginationResult {
  // Convert to numbers and handle invalid inputs
  let pageNum = typeof page === 'string' ? parseInt(page, 10) : (page || 1);
  let pageSizeNum = typeof pageSize === 'string' ? parseInt(pageSize, 10) : (pageSize || 10);

  // Handle NaN, Infinity, and other invalid values
  if (!Number.isFinite(pageNum) || isNaN(pageNum)) {
    pageNum = 1;
  }
  if (!Number.isFinite(pageSizeNum) || isNaN(pageSizeNum)) {
    pageSizeNum = 10;
  }

  // Ensure minimum values
  pageNum = Math.max(1, Math.floor(pageNum));
  pageSizeNum = Math.max(1, Math.min(1000, Math.floor(pageSizeNum)));

  // Calculate skip (offset) - ensure it's never negative
  const skip = Math.max(0, (pageNum - 1) * pageSizeNum);

  return {
    page: pageNum,
    pageSize: pageSizeNum,
    skip,
    limit: pageSizeNum
  };
}

/**
 * Validates a URL and fixes any negative skip parameters
 */
export function fixNegativeSkipInUrl(url: string): string {
  // Quick check to avoid the overhead for the vast majority of calls
  if (!/skip=|skip%3D/i.test(url)) {
    return url;
  }

  // Handle encoded URLs by using regex approach
  if (url.includes('skip%3D')) {
    // For encoded URLs, use regex to fix negative values
    if (url.includes('skip%3D-')) {
      console.warn('Negative skip value detected in URL, fixing:', url);
      const fixedUrl = url.replace(/skip%3D-?\d+/g, 'skip%3D0');
      console.log('Fixed URL:', fixedUrl);
      return fixedUrl;
    }
    return url;
  }

  try {
    // Use URL API for robust parsing (needs absolute URL, so fallback to dummy base if necessary)
    const dummyBase = 'http://fix-negative-skip.local';
    const isAbsolute = /^https?:\/\//i.test(url);
    const parsed = new URL(url, isAbsolute ? undefined : dummyBase);

    // Get all skip parameters
    const skipParams = parsed.searchParams.getAll('skip');
    let hasNegative = false;
    
    // Check if any skip parameter is negative
    for (const skipParam of skipParams) {
      const numericSkip = parseInt(skipParam, 10);
      if (!Number.isNaN(numericSkip) && numericSkip < 0) {
        hasNegative = true;
        break;
      }
    }

    if (hasNegative) {
      console.warn('Negative skip value detected in URL, fixing:', url);
      
      // Remove all skip parameters
      parsed.searchParams.delete('skip');
      
      // Add back non-negative skip parameters as 0
      for (const skipParam of skipParams) {
        const numericSkip = parseInt(skipParam, 10);
        if (!Number.isNaN(numericSkip) && numericSkip < 0) {
          parsed.searchParams.append('skip', '0');
        } else {
          parsed.searchParams.append('skip', skipParam);
        }
      }

      const fixedUrl = isAbsolute ? parsed.toString() : parsed.toString().replace(dummyBase, '');
      console.log('Fixed URL:', fixedUrl);
      return fixedUrl;
    }
  } catch (e) {
    // Fallback to regex approach if URL parsing fails (e.g., malformed URL or browser support issues)
    if (url.includes('skip=-')) {
      console.warn('Negative skip value detected in URL (regex fallback), fixing:', url);
      const fixedUrl = url.replace(/skip=-?\d+/g, 'skip=0');
      console.log('Fixed URL:', fixedUrl);
      return fixedUrl;
    }
  }

  return url;
}

/**
 * Extracts page number from URL search params
 * Always returns a valid page number (minimum 1)
 */
export function getPageFromSearchParams(searchParams: URLSearchParams): number {
  const pageParam = searchParams.get('page');
  const page = pageParam ? parseInt(pageParam, 10) : 1;
  
  // Ensure it's a valid positive number
  if (!Number.isFinite(page) || isNaN(page) || page < 1) {
    return 1;
  }
  
  return Math.floor(page);
}

/**
 * Creates URL search params for pagination
 */
export function createPaginationSearchParams(page: number, additionalParams?: Record<string, string>): URLSearchParams {
  const validPage = Math.max(1, Math.floor(page));
  const params = new URLSearchParams({
    page: validPage.toString(),
    ...additionalParams
  });
  
  return params;
}